import { PublicationShortname } from "@src/app/auth/serviceIdentifier";

type PublicationOverride = {
  enabled?: boolean;
  nlb?: boolean;
};

type NewOnboardingConfig = {
  enabled: boolean;
  enabledForNlb: boolean;
  overrides: Partial<Record<PublicationShortname, PublicationOverride>>;
};

export const newOnboardingConfig: NewOnboardingConfig = {
  enabled: true, // Master switch
  enabledForNlb: false, // NLB users see old modal

  overrides: {
    [PublicationShortname.ST]: { enabled: false },
  },
};

/**
 * Determines whether to show the new onboarding overlay.
 *
 * Logic cascade:
 * 1. Check master switch (enabled) - if false, return false
 * 2. Check publication override - if exists, use it
 * 3. If no override - use global defaults
 *
 * @param publicationId - The current publication
 * @param isNlbUser - Whether the user is an NLB user
 * @returns boolean - Whether to show the new onboarding
 */
export function shouldShowNewOnboarding(
  publicationId: PublicationShortname | null,
  isNlbUser: boolean,
): boolean {
  // Master switch
  if (!newOnboardingConfig.enabled) return false;

  // No publication ID
  if (!publicationId) return false;

  const override = newOnboardingConfig.overrides[publicationId];

  // Check if publication is explicitly disabled
  if (override?.enabled === false) return false;

  // Check NLB user
  if (isNlbUser) {
    // Use override if exists, otherwise use global default
    return override?.nlb ?? newOnboardingConfig.enabledForNlb;
  }

  return true;
}

/**
 * E-Paper Preview (EPPR2-3142) — two-layer feature gate.
 *
 * Layer 1 (this file, product scope): which pubs are preview-capable. TM is the
 * launch pilot. Expanding to another pub = a reviewed change here.
 *
 * Layer 2 (env, operational gate): VITE_EPAPER_PREVIEW_ENABLED, set per
 * environment via GitHub Environments vars (dev/stg true, prd false at launch),
 * baked at build like EPAPER_REQUIRE_SIGNED_COOKIE. Keeps the prd go-live off
 * the shared release train and gives a redeploy-only kill switch. An
 * undefined/empty var reads as OFF (safe default).
 *
 * Both must be true for a pub to enter preview mode. isPreviewEnabledForPub is
 * the single gate the UI reads.
 */
export const previewPilotConfig: Partial<
  Record<PublicationShortname, boolean>
> = {
  [PublicationShortname.TM]: true,
};

/**
 * Subscribe destinations for the preview CTAs (bottom bar + archive/calendar
 * gate modal). Kept separate from serviceIdentifier's getSubscribeNowUrlByPubId
 * (the SubscribePage eShop links). Points at the publication subscribe landing
 * page carrying the portal-paywall UTM tags from the GA Tagging Plan
 * (IRISX-45). TM only for the pilot.
 */
export const PREVIEW_SUBSCRIBE_URL: Partial<
  Record<PublicationShortname, string>
> = {
  [PublicationShortname.TM]:
    "https://subscribe.sph.com.sg/publications-tm/?utm_source=web_epaper_preview_portal_paywall&utm_medium=internal&utm_campaign=tm",
};

/**
 * The single gate the preview UI reads: env operational gate AND per-pub
 * product scope. Returns false for null pub, gate off, or non-pilot pub.
 */
export function isPreviewEnabledForPub(
  pubId: PublicationShortname | null,
): boolean {
  if (!pubId) {
    return false;
  }
  if (import.meta.env.VITE_EPAPER_PREVIEW_ENABLED !== "true") {
    return false;
  }
  return previewPilotConfig[pubId] === true;
}
